/**
 =========================================================
 * Soft UI Dashboard PRO React - v2.0.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
 * Copyright 2021 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

import { useState, useEffect } from "react";

// react-router components
import { useLocation, Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

// Custom styles for DashboardNavbar
import styles from "examples/Navbars/DashboardNavbar/styles";

// Soft UI Dashboard PRO React context
import { useSoftUIController } from "context";
import { useDispatch } from "react-redux";
import MenuItem from "@mui/material/MenuItem";
import { SvgIcon } from "@mui/material";
import { logout } from "../../../redux/Middleware/AuthThunk";
import Breadcrumbs from "../../../components/Breadcrumbs";

function DashboardNavbar({ absolute, light, isMini, breadcrumbTitle, breadcrumbRoutes }) {
  const appDispatch = useDispatch();
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const classes = styles({ transparentNavbar, absolute, light, isMini });
  const [settings, setSettings] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }
    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      if (!transparentNavbar && fixedNavbar && window.scrollY === 0) {
        dispatch({
          type: "TRANSPARENT_NAVBAR",
          value: true,
        });
      }
      if (transparentNavbar && fixedNavbar && window.scrollY !== 0) {
        dispatch({
          type: "TRANSPARENT_NAVBAR",
          value: false,
        });
      }
    }

    /**
     The event listener that's calling the handleTransparentNavbar function when
     scrolling the window.
     */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar, transparentNavbar]);

  const handleMiniSidenav = () => dispatch({ type: "MINI_SIDENAV", value: !miniSidenav });
  const handleConfiguratorOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => setAnchorEl(null);

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={anchorEl}
      // anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={Boolean(anchorEl)}
      onClose={handleCloseMenu}
      sx={{ marginTop: "1rem" }}
    >
      <MenuItem>
        <Link to="/profile">
          <SuiTypography variant="button" fontWeight="light" textColor={light ? "white" : "dark"}>
            Profile
          </SuiTypography>
        </Link>
      </MenuItem>
      <MenuItem>
        <Link to="/profile">
          <SuiTypography variant="button" fontWeight="light" textColor={light ? "white" : "dark"}>
            Users
          </SuiTypography>
        </Link>
      </MenuItem>
    </Menu>
  );

  const handleLogout = async (e) => {
    e.preventDefault();
    appDispatch(logout());
  };

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      className={classes.navbar}
    >
      <Toolbar className={classes.navbar_container}>
        <SuiBox customClass={classes.navbar_row} color="inherit" mb={{ xs: 1, md: 0 }}>
          <Icon
            className={classes.navbar_desktop_menu}
            fontSize="medium"
            onClick={handleMiniSidenav}
          >
            {miniSidenav ? "menu_open" : "menu"}
          </Icon>
        </SuiBox>
        <SuiBox customClass={classes.navbar_breadcrumbs}>
          <Breadcrumbs icon="home" title={breadcrumbTitle} route={breadcrumbRoutes} />
        </SuiBox>
        {isMini ? null : (
          <SuiBox customClass={classes.navbar_right}>
            <SuiBox
              color={light ? "white" : "inherit"}
              customClass={classes.navbar_section_desktop}
            >
              <IconButton
                className={classes.navbar_icon_button}
                size="small"
                onClick={handleLogout}
              >
                <SvgIcon>
                  <svg xmlSpace="preserve" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M11.608 22.27H5.576a2.973 2.973 0 0 1-2.97-2.969V4.763c0-1.64 1.336-2.97 2.97-2.97h6.13a.66.66 0 0 0 .663-.662.66.66 0 0 0-.663-.662h-6.13a4.3 4.3 0 0 0-4.294 4.294v14.538a4.3 4.3 0 0 0 4.294 4.295h6.032a.663.663 0 0 0 0-1.325z" />
                    <path d="m22.524 11.566-4.211-4.211a.66.66 0 0 0-.938 0 .66.66 0 0 0 0 .937l3.083 3.082H7.014a.66.66 0 0 0-.662.663.66.66 0 0 0 .662.663h13.444l-3.083 3.082a.66.66 0 0 0 0 .937c.128.128.3.197.467.197a.647.647 0 0 0 .466-.197l4.211-4.21a.662.662 0 0 0 .005-.943z" />
                  </svg>
                </SvgIcon>
                <SuiTypography
                  variant="button"
                  fontWeight="medium"
                  textColor={light ? "white" : "dark"}
                >
                  Sign out
                </SuiTypography>
              </IconButton>
              <IconButton
                size="small"
                color="inherit"
                className={classes.navbar_mobile_menu}
                onClick={handleMiniSidenav}
              >
                <Icon className={light ? "text-white" : "text-dark"}>
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
            </SuiBox>
            <SuiBox>
              <IconButton
                size="small"
                color="inherit"
                className={classes.navbar_icon_button}
                onClick={handleConfiguratorOpen}
              >
                <SvgIcon>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="m22.642 9.465-1.66-.282a9.37 9.37 0 0 0-.666-1.61l.978-1.368a1.456 1.456 0 0 0-.158-1.882l-1.472-1.472a1.45 1.45 0 0 0-1.877-.158l-1.373.978a9.273 9.273 0 0 0-1.67-.686l-.276-1.64a1.459 1.459 0 0 0-1.442-1.22h-2.08c-.716 0-1.323.514-1.442 1.22l-.286 1.68a9.14 9.14 0 0 0-1.605.676l-1.359-.978a1.45 1.45 0 0 0-.85-.272c-.39 0-.76.154-1.031.43L2.896 4.353a1.462 1.462 0 0 0-.158 1.882l.988 1.388a9.153 9.153 0 0 0-.657 1.615l-1.64.276a1.459 1.459 0 0 0-1.22 1.442v2.08c0 .716.514 1.323 1.22 1.442l1.68.286c.172.554.4 1.092.676 1.606l-.973 1.353a1.456 1.456 0 0 0 .158 1.882l1.472 1.471a1.45 1.45 0 0 0 1.877.158l1.387-.987a9.343 9.343 0 0 0 1.561.642l.277 1.66c.118.706.726 1.22 1.442 1.22h2.084c.716 0 1.324-.514 1.442-1.22l.282-1.66a9.37 9.37 0 0 0 1.61-.667l1.368.978c.247.178.543.272.85.272.39 0 .755-.154 1.032-.43l1.472-1.472a1.462 1.462 0 0 0 .158-1.882l-.978-1.373a9.303 9.303 0 0 0 .667-1.61l1.66-.276a1.459 1.459 0 0 0 1.22-1.443v-2.079a1.441 1.441 0 0 0-1.211-1.442zm-.114 3.521a.128.128 0 0 1-.108.129l-2.075.345a.663.663 0 0 0-.533.49 7.907 7.907 0 0 1-.86 2.069.668.668 0 0 0 .03.726l1.22 1.719c.035.05.03.123-.015.168l-1.471 1.471a.125.125 0 0 1-.094.04.121.121 0 0 1-.074-.025l-1.714-1.22a.667.667 0 0 0-.726-.03 7.907 7.907 0 0 1-2.07.86.656.656 0 0 0-.489.533l-.35 2.075a.128.128 0 0 1-.129.109h-2.08a.128.128 0 0 1-.128-.11l-.345-2.074a.663.663 0 0 0-.49-.533 8.21 8.21 0 0 1-2.024-.83.684.684 0 0 0-.336-.089.65.65 0 0 0-.385.124l-1.729 1.23a.147.147 0 0 1-.074.024.132.132 0 0 1-.094-.04l-1.472-1.47a.134.134 0 0 1-.015-.169l1.215-1.704a.676.676 0 0 0 .03-.73 7.832 7.832 0 0 1-.87-2.065.676.676 0 0 0-.533-.49l-2.089-.355a.128.128 0 0 1-.108-.128v-2.08c0-.064.044-.118.108-.128l2.06-.346a.668.668 0 0 0 .538-.494 7.899 7.899 0 0 1 .845-2.074.66.66 0 0 0-.035-.721L3.83 5.464a.135.135 0 0 1 .015-.168l1.472-1.472a.125.125 0 0 1 .094-.04c.03 0 .054.01.074.026l1.704 1.215a.676.676 0 0 0 .73.03 7.832 7.832 0 0 1 2.065-.87.676.676 0 0 0 .49-.533l.355-2.09a.128.128 0 0 1 .128-.108h2.08c.064 0 .118.044.128.108l.346 2.06a.668.668 0 0 0 .494.538c.745.188 1.457.48 2.119.87a.668.668 0 0 0 .726-.03l1.704-1.225a.147.147 0 0 1 .074-.025c.02 0 .059.005.093.04l1.472 1.472c.045.044.05.113.015.168l-1.22 1.713a.667.667 0 0 0-.03.727c.386.647.672 1.343.86 2.069.064.257.276.45.533.489l2.075.35c.064.01.108.065.108.129v2.08z" />
                    <path d="M12.033 6.842a5.105 5.105 0 0 0-5.102 5.102 5.105 5.105 0 0 0 5.102 5.102 5.105 5.105 0 0 0 5.102-5.102 5.105 5.105 0 0 0-5.102-5.102zm0 8.87a3.771 3.771 0 0 1-3.768-3.768c0-2.08 1.689-3.768 3.768-3.768 2.08 0 3.768 1.689 3.768 3.768 0 2.08-1.689 3.769-3.768 3.769z" />
                  </svg>
                </SvgIcon>
              </IconButton>
            </SuiBox>
          </SuiBox>
        )}
        <Menu
          anchorEl={anchorEl}
          // anchorReference={null}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          sx={{ marginTop: "1rem", padding: "1rem 0" }}
        >
          <Link to="/profile">
            <MenuItem sx={{ padding: "10px", "&.hover": { backgroundColor: "#17c1e8" } }}>
              <SvgIcon class={classes.navbar_icon_menu_item}>
                <svg xmlSpace="preserve" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M12 1.178a5.248 5.248 0 0 0-5.242 5.244A5.247 5.247 0 0 0 12 11.664a5.247 5.247 0 0 0 5.242-5.242A5.248 5.248 0 0 0 12 1.178zm0 9.75a4.512 4.512 0 0 1-4.506-4.506A4.513 4.513 0 0 1 12 1.914a4.513 4.513 0 0 1 4.506 4.508A4.512 4.512 0 0 1 12 10.928zM17.194 13.335l-.356-.107L12 16.873l-4.837-3.646-.356.108C.221 15.31.227 22.028.228 22.095l.009.727h23.526l.009-.727c.002-.067.007-6.784-6.578-8.76zM.964 22.086c-.001-.274.058-6.247 6.054-8.046l4.54 3.42.442.334.443-.334 4.539-3.42c6.007 1.802 6.055 7.793 6.054 8.046z" />
                </svg>
              </SvgIcon>
              <SuiTypography
                variant="button"
                fontWeight="light"
                textColor={light ? "white" : "dark"}
              >
                Profile
              </SuiTypography>
            </MenuItem>
          </Link>
          <Link to="/users">
            <MenuItem sx={{ padding: "10px", "&.hover": { backgroundColor: "#17c1e8" } }}>
              <SvgIcon class={classes.navbar_icon_menu_item}>
                <svg xmlSpace="preserve" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M9.53 6.575c0 1.363.534 2.938 1.428 4.211 1.012 1.443 2.291 2.238 3.601 2.238s2.589-.795 3.602-2.238c.893-1.273 1.427-2.848 1.427-4.211a5.034 5.034 0 0 0-5.029-5.029 5.034 5.034 0 0 0-5.028 5.029zm5.03-4.209a4.213 4.213 0 0 1 4.208 4.209c0 1.183-.49 2.617-1.279 3.74-.842 1.2-1.91 1.89-2.93 1.89-1.02 0-2.088-.69-2.93-1.89-.789-1.123-1.278-2.557-1.278-3.74a4.213 4.213 0 0 1 4.208-4.209z" />
                  <path d="M21.928 15.273A8.778 8.778 0 0 0 18.32 12.1a.41.41 0 0 0-.466.08c-.988.985-2.128 1.506-3.296 1.506-1.169 0-2.308-.521-3.295-1.506a.41.41 0 0 0-.466-.08 8.779 8.779 0 0 0-3.608 3.174 8.728 8.728 0 0 0-1.391 4.74v.006a.41.41 0 0 0 .135.304 12.814 12.814 0 0 0 8.625 3.327c3.196 0 6.26-1.182 8.625-3.327a.41.41 0 0 0 .135-.304v-.007a8.73 8.73 0 0 0-1.391-4.739zm-7.369 7.557a11.996 11.996 0 0 1-7.938-2.993 7.906 7.906 0 0 1 1.259-4.12 7.96 7.96 0 0 1 3.019-2.752c1.095 1.008 2.355 1.54 3.66 1.54 1.304 0 2.564-.532 3.66-1.54a7.96 7.96 0 0 1 3.02 2.752 7.907 7.907 0 0 1 1.258 4.12 11.996 11.996 0 0 1-7.938 2.993zM8.293 10.363c.46 0 .919-.112 1.364-.333a.41.41 0 0 0-.365-.735c-.33.165-.667.248-.999.248-.861 0-1.765-.585-2.481-1.605-.674-.96-1.092-2.184-1.092-3.194A3.578 3.578 0 0 1 8.293 1.17c.93 0 1.81.356 2.48 1.001a.41.41 0 0 0 .57-.59 4.37 4.37 0 0 0-3.05-1.23A4.398 4.398 0 0 0 3.9 4.743c0 1.187.464 2.557 1.24 3.665.885 1.26 2.004 1.954 3.153 1.954z" />
                  <path d="M4.214 18.012a10.296 10.296 0 0 1-2.71-1.712 6.763 6.763 0 0 1 1.075-3.498 6.81 6.81 0 0 1 2.548-2.336c.949.862 2.038 1.316 3.166 1.316a4.33 4.33 0 0 0 1.967-.481.41.41 0 0 0-.37-.732 3.517 3.517 0 0 1-1.597.393c-.993 0-1.962-.443-2.803-1.282a.41.41 0 0 0-.465-.08 7.628 7.628 0 0 0-3.135 2.758 7.585 7.585 0 0 0-1.209 4.118v.006a.41.41 0 0 0 .135.304 11.12 11.12 0 0 0 3.074 1.98.408.408 0 0 0 .538-.215.41.41 0 0 0-.214-.539zM5.382 18.453a.414.414 0 0 0-.488.015.414.414 0 0 0-.125.478.414.414 0 0 0 .379.253.414.414 0 0 0 .394-.3.412.412 0 0 0-.16-.446z" />
                </svg>
              </SvgIcon>
              <SuiTypography
                variant="button"
                fontWeight="light"
                textColor={light ? "white" : "dark"}
              >
                Users
              </SuiTypography>
            </MenuItem>
          </Link>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
  breadcrumbTitle: "",
  breadcrumbRoutes: [],
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  breadcrumbTitle: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  breadcrumbRoutes: PropTypes.array,
};

export default DashboardNavbar;
